import React, {useState, Fragment} from 'react';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {ExpandMore as ExpandMoreIcon} from '@material-ui/icons';
import './Chart.css';
import {CustomToolTip} from './CustomToolTip';
import {meterUnitConversationRateAction} from '../../utils/meterUnitConversion';
import {DashboardDailyUsageBarChart} from './DashboardDailyUsageBarChart';
import {ChartViewToggle} from './ChartViewToggle';
import {accordionA11yProps} from '../utils/accordionA11yProps';
import {AverageUsageSummary} from './AverageUsageSummary';

export const DashboardUsageChart = ({meter}) => {
  const [view, setView] = useState('bar');
  // Lifted here so the open/closed state survives switching views, and so a
  // toggle interaction can always reveal the chart. Only the chevron closes it.
  const [expanded, setExpanded] = useState(false);

  const handleViewChange = (next) => {
    setView(next);
    setExpanded(true);
  };

  const sharedProps = {
    meter,
    view,
    onViewChange: handleViewChange,
    expanded,
    onToggleExpand: () => setExpanded((prev) => !prev),
  };

  if (view === 'bar') {
    return <DashboardDailyUsageBarChart {...sharedProps} />;
  }
  return <DashboardUsageLineChart {...sharedProps} />;
};

const DashboardUsageLineChart = ({
  meter,
  view,
  onViewChange,
  expanded,
  onToggleExpand,
}) => {
  let tickDays = [0, 1, 2, 3, 4, 5, 6, 7];
  let tickDaysTime = tickDays.map((t) => t * 1000 * 60 * 60 * 24);
  let convertedReadings = [];
  let experimentReadings = [];
  let baselineReadings = [];
  let experimentStart = meter.experimentStart
    ? new Date(meter.experimentStart).valueOf()
    : undefined;
  const isUtilityPortal = meter.dataSource === 'utility-portal';
  let baselineAverage = 0;
  let experimentAverage = 0;

  const useStyles = makeStyles(({palette}) => ({
    usageChart: {
      width: '100%',
      height: '75vh',
      backgroundColor: palette.meterTypeColor,
    },
    heading: {
      fontSize: '1.7em',
    },
    usageChartHeader: {
      color: palette.meterTypeTextColor,
      backgroundColor: palette.meterTypeColor,
      '& svg': {
        color: palette.meterTypeTextColor,
      },
      textTransform: 'capitalize',
    },
  }));

  const classes = useStyles(meter);
  const a11y = accordionA11yProps({
    id: 'usage-chart',
    expanded,
    label: expanded ? 'collapse usage chart' : 'expand usage chart',
  });

  if (meter.readings) {
    const validReadings = meter.readings.filter((reading) => {
      return !reading.deleted;
    });
    baselineReadings = validReadings.filter((reading) => {
      reading.time = new Date(reading.time).valueOf();
      // If experiment start is undefined, all comparisons
      // are false, so nothing is returned.
      return reading.time <= experimentStart;
    });
    experimentReadings = validReadings.filter((reading) => {
      reading.time = new Date(reading.time).valueOf();
      // If experiment start is undefined, all comparisons
      // are false, so nothing is returned.
      return reading.time > experimentStart;
    });
    const ONE_DAY = 1000 * 60 * 60 * 24;
    convertedReadings = [];

    // Baseline phase: readings up to the experiment start (or all readings when
    // no experiment has started yet).
    const baseSeries = experimentStart ? baselineReadings : validReadings;
    if (baseSeries.length) {
      if (isUtilityPortal) {
        // Utility-portal `value` is a running sum from zero, so plot it
        // directly and anchor the line with an artificial (0, 0) point one
        // interval before the first reading, so the first day's usage shows.
        const interval =
          baseSeries.length > 1
            ? baseSeries[1].time - baseSeries[0].time
            : ONE_DAY;
        const startTime = baseSeries[0].time - interval;
        convertedReadings.push({
          time: startTime,
          plotTime: 0,
          baseline: 0,
          yValue: 0,
        });
        baseSeries.forEach((r) => {
          convertedReadings.push({
            ...r,
            plotTime: r.time - startTime,
            baseline: r.value,
            yValue: r.value,
          });
        });
      } else {
        // Meter-based `value` is an absolute cumulative reading, so usage is
        // measured from the first reading; the baseline necessarily starts at
        // zero (there is no earlier reading to anchor against).
        const startTime = baseSeries[0].time;
        const startValue = baseSeries[0].value;
        baseSeries.forEach((r) => {
          convertedReadings.push({
            ...r,
            plotTime: r.time - startTime,
            baseline: r.value - startValue,
            yValue: r.value - startValue,
          });
        });
      }
    }

    // Experiment phase (both data sources): anchor at the experiment boundary
    // using the last baseline reading as the zero reference, so the first
    // experiment reading reflects real usage since the experiment began instead
    // of collapsing to zero.
    if (experimentStart && experimentReadings.length) {
      const hasBaseline = baseSeries.length > 0;
      const boundaryTime = hasBaseline
        ? baseSeries[baseSeries.length - 1].time
        : experimentStart;
      // With no baseline readings there is no earlier reference: utility-portal
      // values are already from zero, meter-based ones fall back to the first
      // experiment reading (its first point is then unavoidably zero).
      const boundaryValue = hasBaseline
        ? baseSeries[baseSeries.length - 1].value
        : isUtilityPortal
        ? 0
        : experimentReadings[0].value;
      convertedReadings.push({
        time: boundaryTime,
        plotTime: 0,
        experiment: 0,
        yValue: 0,
      });
      experimentReadings.forEach((r) => {
        convertedReadings.push({
          ...r,
          plotTime: r.time - boundaryTime,
          experiment: r.value - boundaryValue,
          yValue: r.value - boundaryValue,
        });
      });
    }
  }
  if (meter.expectedUsage) {
    let convertedExpectedUsage = meter.expectedUsage;
    if (meter.unit && meter.expectedUsageUnit) {
      convertedExpectedUsage = meterUnitConversationRateAction(
        meter.unit,
        meter.expectedUsageUnit,
        meter.expectedUsage,
      );
    }
    for (let i = 0; i < tickDays.length; i++) {
      convertedReadings.push({
        plotTime: tickDaysTime[i],
        expected: convertedExpectedUsage * tickDays[i],
        yValue: convertedExpectedUsage * tickDays[i],
      });
    }
  }

  if (
    baselineReadings.length &&
    baselineReadings[0].value &&
    baselineReadings.length > 1
  ) {
    baselineAverage =
      (baselineReadings[baselineReadings.length - 1].value -
        baselineReadings[0].value) /
      (baselineReadings.length - 1);
  }
  if (
    experimentReadings.length &&
    experimentReadings[0].value &&
    experimentReadings.length > 1
  ) {
    experimentAverage =
      (experimentReadings[experimentReadings.length - 1].value -
        experimentReadings[0].value) /
      (experimentReadings.length - 1);
  }

  return (
    <Accordion expanded={expanded} onChange={onToggleExpand}>
      <AccordionSummary
        className={classes.usageChartHeader}
        expandIcon={<ExpandMoreIcon />}
        {...a11y.summaryProps}
      >
        <Typography
          className={classes.heading}
        >{`${meter.type} Usage Chart`}</Typography>
        <ChartViewToggle view={view} onChange={onViewChange} />
      </AccordionSummary>
      <AccordionDetails className={classes.usageChart} {...a11y.detailsProps}>
        <Fragment>
          <div style={{position: 'relative', width: '100%', height: '100%'}}>
            <ResponsiveContainer>
              <LineChart
                data={convertedReadings}
                margin={{top: 15, right: 30, left: 20, bottom: 5}}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="white"
                  strokeOpacity="0.6"
                  strokeDasharray="2 2 2"
                />

                <XAxis
                  dataKey="plotTime"
                  type="number"
                  padding={{left: 40, right: 40}}
                  stroke="white"
                  style={{fontSize: '1em'}}
                  domain={['dataMin', 'dataMax']}
                  tickLine={{stroke: 'white'}}
                  axisLine={{stroke: 'white'}}
                  ticks={tickDaysTime}
                  tickFormatter={(t) => t / 1000 / 60 / 60 / 24}
                >
                  <Label
                    value="24-hour periods"
                    fill="white"
                    position="insideBottomRight"
                    offset={0}
                  />
                </XAxis>

                <YAxis
                  type="number"
                  stroke="white"
                  style={{fontSize: '1em'}}
                  domain={['auto', 'auto']}
                  tickLine={false}
                  padding={{top: 20, bottom: 30}}
                  axisLine={false}
                >
                  <Label
                    value={meter.unit}
                    angle={-90}
                    position="insideLeft"
                    fill="white"
                  />
                </YAxis>

                <Tooltip
                  active={false}
                  content={<CustomToolTip />}
                  cursor={false}
                  meter={meter}
                />

                <Legend align="left" wrapperStyle={{color: 'white'}} />

                <Line
                  isAnimationActive={false}
                  dataKey="expected"
                  type="monotone"
                  stroke="#F9F935"
                  strokeWidth="2px"
                  dot={{stroke: '#F9F935', strokeWidth: 2, r: 5}}
                  activeDot={{stroke: '#F9F935', strokeWidth: 3, r: 7}}
                />

                <Line
                  isAnimationActive={false}
                  dataKey="baseline"
                  type="monotone"
                  stroke="black"
                  strokeWidth="2px"
                  dot={{stroke: 'black', strokeWidth: 2, r: 5}}
                  activeDot={{stroke: 'black', strokeWidth: 3, r: 7}}
                />

                <Line
                  isAnimationActive={false}
                  dataKey="experiment"
                  type="monotone"
                  stroke="white"
                  strokeWidth="2px"
                  dot={{stroke: 'white', strokeWidth: 2, r: 5}}
                  activeDot={{stroke: 'white', strokeWidth: 3, r: 7}}
                />
              </LineChart>
            </ResponsiveContainer>
            <AverageUsageSummary
              baselineAverage={baselineAverage}
              experimentAverage={experimentAverage}
              unit={meter.unit}
            />
          </div>
        </Fragment>
      </AccordionDetails>
    </Accordion>
  );
};
